import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import Modal from 'react-modal';

import {
    ModalEconomiaContenido,
    ModalEconomiaCabecera,
    ModalEconomiaTitulo,
    ModalEconomiaSubtitulo,
    BotonCerrarModal,

    ModalSeccion,
    ModalSeccionTitulo,
    ModalAcciones,
    ModalScrollContenido,

    BotonPrincipal,
    BotonSecundario,

    CobroFormulario,
    CobroCamposGrid,
    CobroCampo,
    CobroLabel,
    CobroInput,
    CobroSelect,
    CobroTextarea,

    CargosLista,
    CargoSeleccionable,
    CargoCheckbox,
    CargoDatos,
    CargoFecha,
    CargoDetalle,
    CargoSaldo,
    CargoSaldoLabel,
    CargoSaldoValor,

    CobroTotalSeleccionado,
    CobroTotalLabel,
    CobroTotalValor,

    SinInformacion
} from '../../Css/StyledEstadisticasCobros.js';


Modal.setAppElement('#root');


const formatMoney = (value) => {

    return new Intl.NumberFormat(
        'es-AR',
        {
            style: 'currency',
            currency: 'ARS',
            maximumFractionDigits: 0
        }
    ).format(
        Number(value || 0)
    );

};


const formatDate = (value) => {

    if (!value) {
        return '—';
    }

    const [
        year,
        month,
        day
    ] = String(value)
        .substring(0, 10)
        .split('-');

    return `${day}/${month}/${year}`;
};


const getFechaHoy = () => {

    const hoy =
        new Date();

    const year =
        hoy.getFullYear();

    const month =
        String(
            hoy.getMonth() + 1
        ).padStart(2, '0');

    const day =
        String(
            hoy.getDate()
        ).padStart(2, '0');

    return `${year}-${month}-${day}`;
};


/*
====================================
REGISTRAR COBRO
====================================
*/

const RegistrarCobroModal = ({
    isOpen,
    onClose,
    cargos,
    onRegistrar,
    guardando,
    datosIniciales = null
}) => {

    const [
        cargosSeleccionados,
        setCargosSeleccionados
    ] = useState([]);


    const [
        importe,
        setImporte
    ] = useState('');


    const [
        fechaPago,
        setFechaPago
    ] = useState(
        getFechaHoy()
    );


    const [
        medioPago,
        setMedioPago
    ] = useState(
        'Transferencia'
    );


    const [
        observacion,
        setObservacion
    ] = useState('');

    /*
    ====================================
    MODO DEL FORMULARIO
    ====================================

    Si hay datos iniciales, el formulario
    se abrió como parte de la corrección
    de un cobro anterior.
    ====================================
    */

    const esCorreccion =
        Boolean(
            datosIniciales
        );

    /*
    ====================================
    DATOS INICIALES DEL COBRO
    ====================================

    Normalmente el formulario empieza vacío.

    Cuando venimos desde "Corregir cobro",
    podemos precargar los datos del pago
    que acaba de ser anulado.
    ====================================
    */

    useEffect(() => {

        if (!isOpen) {
            return;
        }


        /*
        ====================================
        COBRO NUEVO
        ====================================
        */

        if (!datosIniciales) {

            setCargosSeleccionados([]);
            setImporte('');
            setFechaPago(
                getFechaHoy()
            );
            setMedioPago(
                'Transferencia'
            );
            setObservacion('');

            return;
        }


        /*
        ====================================
        CORRECCIÓN DE COBRO
        ====================================
        */

        setCargosSeleccionados(
            Array.isArray(
                datosIniciales.cargos_ids
            )
                ? datosIniciales.cargos_ids
                : []
        );


        setImporte(
            datosIniciales.importe !==
            null &&
            datosIniciales.importe !==
            undefined

                ? String(
                    datosIniciales.importe
                )

                : ''
        );


        setFechaPago(
            datosIniciales.fecha_pago ||
            getFechaHoy()
        );


        setMedioPago(
            datosIniciales.medio_pago ||
            'Transferencia'
        );


        setObservacion(
            datosIniciales.observacion ||
            ''
        );

    }, [
        isOpen,
        datosIniciales
    ]);


    /*
    ====================================
    CARGOS DISPONIBLES PARA COBRAR
    ====================================

    La lista ya viene directamente desde
    Economía, no desde los turnos visibles
    del período.

    Por eso puede incluir deuda anterior.
    ====================================
    */

    const cargosDisponibles =
        useMemo(() => {

            if (
                !Array.isArray(cargos)
            ) {
                return [];
            }


            return cargos.filter(
                (cargo) =>

                    cargo.cobrable &&

                    Number(
                        cargo.saldo_pendiente ||
                        0
                    ) > 0
            );

        }, [
            cargos
        ]);


    /*
    ====================================
    TOTAL SELECCIONADO
    ====================================
    */

    const totalSeleccionado =
        useMemo(() => {

            return cargosDisponibles
                .filter(
                    (cargo) =>
                        cargosSeleccionados.includes(
                            cargo.id
                        )
                )
                .reduce(
                    (
                        total,
                        cargo
                    ) =>
                        total +
                        Number(
                            cargo.saldo_pendiente ||
                            0
                        ),
                    0
                );

        }, [
            cargosDisponibles,
            cargosSeleccionados
        ]);


    /*
    ====================================
    SELECCIONAR CARGO
    ====================================
    */

    const toggleCargo = (
        cargoId
    ) => {

        setCargosSeleccionados(
            (actuales) => {

                if (
                    actuales.includes(
                        cargoId
                    )
                ) {

                    return actuales.filter(
                        (id) =>
                            id !== cargoId
                    );

                }


                return [
                    ...actuales,
                    cargoId
                ];

            }
        );

    };

    /*
    ====================================
    GUARDAR COBRO
    ====================================
    */

    const handleRegistrar = async () => {

        if (
            typeof onRegistrar !==
            'function'
        ) {
            return;
        }


        const guardado =
            await onRegistrar({
                fecha_pago:
                    fechaPago,

                importe,

                medio_pago:
                    medioPago,

                observacion,

                cargos_ids:
                    cargosSeleccionados
            });


        /*
        ====================================
        LIMPIAR FORMULARIO
        ====================================

        Solo limpiamos si realmente se
        pudo registrar el cobro.
        ====================================
        */

        if (guardado) {

            setCargosSeleccionados([]);
            setImporte('');
            setFechaPago(
                getFechaHoy()
            );
            setMedioPago(
                'Transferencia'
            );
            setObservacion('');

        }

    };


    /*
    ====================================
    CERRAR
    ====================================
    */

    const handleCerrar = () => {

        setCargosSeleccionados([]);
        setImporte('');
        setFechaPago(
            getFechaHoy()
        );
        setMedioPago(
            'Transferencia'
        );
        setObservacion('');

        onClose();

    };


    return (

        <Modal
            isOpen={isOpen}

            onRequestClose={
                handleCerrar
            }

            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}

            style={{
                overlay: {
                    zIndex: 2000,

                    backgroundColor:
                        'rgba(0, 0, 0, 0.45)',

                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    padding: '1rem'
                },

                content: {
                    position: 'relative',

                    inset: 'auto',

                    border: 'none',

                    background:
                        'transparent',

                    padding: 0,

                    width: '100%',
                    maxWidth: '700px',

                    maxHeight: '90vh',

                    overflow: 'hidden',

                    boxSizing:
                        'border-box'
                }
            }}
        >

            <ModalEconomiaContenido>


                <ModalEconomiaCabecera>

                    <div>

                        <ModalEconomiaTitulo>
                            {
                                esCorreccion
                                    ? 'Corregir cobro'
                                    : 'Registrar cobro'
                            }
                        </ModalEconomiaTitulo>

                        <ModalEconomiaSubtitulo>
                            {
                                esCorreccion

                                    ? 'Revisá los datos del cobro anterior y modificá lo que necesites antes de registrarlo nuevamente.'

                                    : 'Seleccioná las prestaciones que querés incluir en este cobro.'
                            }
                        </ModalEconomiaSubtitulo>

                    </div>


                    <BotonCerrarModal
                        type="button"
                        onClick={
                            handleCerrar
                        }
                    >
                        ×
                    </BotonCerrarModal>

                </ModalEconomiaCabecera>

                <ModalScrollContenido>
                    <CobroFormulario>


                        <ModalSeccion>

                            <ModalSeccionTitulo>
                                Prestaciones pendientes
                            </ModalSeccionTitulo>


                            {cargosDisponibles.length === 0 ? (

                                <SinInformacion>
                                    Este paciente no tiene
                                    prestaciones pendientes de cobro.
                                </SinInformacion>

                            ) : (

                                <CargosLista>

                                    {cargosDisponibles.map(
                                        (cargo) => {

                                            const seleccionado =
                                                cargosSeleccionados.includes(
                                                    cargo.id
                                                );


                                            return (

                                                <CargoSeleccionable
                                                    key={
                                                        cargo.id
                                                    }

                                                    $seleccionado={
                                                        seleccionado
                                                    }
                                                >

                                                    <CargoCheckbox
                                                        type="checkbox"

                                                        checked={
                                                            seleccionado
                                                        }

                                                        onChange={() =>
                                                            toggleCargo(
                                                                cargo.id
                                                            )
                                                        }
                                                    />


                                                    <CargoDatos>

                                                        <CargoFecha>

                                                            {
                                                                formatDate(
                                                                    cargo.fecha_prestacion
                                                                )
                                                            }

                                                        </CargoFecha>


                                                        <CargoDetalle>

                                                            {
                                                                cargo.concepto ||
                                                                cargo.tipo_evento ||
                                                                'Prestación'
                                                            }

                                                            {' · '}

                                                            {
                                                                formatMoney(
                                                                    cargo.importe
                                                                )
                                                            }

                                                        </CargoDetalle>

                                                    </CargoDatos>


                                                    <CargoSaldo>

                                                        <CargoSaldoLabel>
                                                            Pendiente
                                                        </CargoSaldoLabel>

                                                        <CargoSaldoValor>

                                                            {
                                                                formatMoney(
                                                                    cargo.saldo_pendiente
                                                                )
                                                            }

                                                        </CargoSaldoValor>

                                                    </CargoSaldo>

                                                </CargoSeleccionable>

                                            );

                                        }
                                    )}

                                </CargosLista>

                            )}


                            {cargosSeleccionados.length > 0 ? (

                                <CobroTotalSeleccionado>

                                    <CobroTotalLabel>

                                        Total pendiente
                                        seleccionado

                                    </CobroTotalLabel>


                                    <CobroTotalValor>

                                        {
                                            formatMoney(
                                                totalSeleccionado
                                            )
                                        }

                                    </CobroTotalValor>

                                </CobroTotalSeleccionado>

                            ) : null}

                        </ModalSeccion>


                        <ModalSeccion>

                            <ModalSeccionTitulo>
                                Datos del cobro
                            </ModalSeccionTitulo>


                            <CobroCamposGrid>


                                <CobroCampo>

                                    <CobroLabel>
                                        Importe recibido
                                    </CobroLabel>

                                    <CobroInput
                                        type="number"

                                        min="0"

                                        step="0.01"

                                        value={
                                            importe
                                        }

                                        onChange={
                                            (e) =>
                                                setImporte(
                                                    e.target.value
                                                )
                                        }
                                    />

                                </CobroCampo>


                                <CobroCampo>

                                    <CobroLabel>
                                        Fecha del cobro
                                    </CobroLabel>

                                    <CobroInput
                                        type="date"

                                        value={
                                            fechaPago
                                        }

                                        onChange={
                                            (e) =>
                                                setFechaPago(
                                                    e.target.value
                                                )
                                        }
                                    />

                                </CobroCampo>


                                <CobroCampo>

                                    <CobroLabel>
                                        Medio de pago
                                    </CobroLabel>

                                    <CobroSelect
                                        value={
                                            medioPago
                                        }

                                        onChange={
                                            (e) =>
                                                setMedioPago(
                                                    e.target.value
                                                )
                                        }
                                    >

                                        <option value="Transferencia">
                                            Transferencia
                                        </option>

                                        <option value="Efectivo">
                                            Efectivo
                                        </option>

                                        <option value="Mercado Pago">
                                            Mercado Pago
                                        </option>

                                    </CobroSelect>

                                </CobroCampo>

                            </CobroCamposGrid>


                            <CobroCampo
                                style={{
                                    marginTop:
                                        '0.7rem'
                                }}
                            >

                                <CobroLabel>
                                    Observación
                                </CobroLabel>

                                <CobroTextarea
                                    value={
                                        observacion
                                    }

                                    onChange={
                                        (e) =>
                                            setObservacion(
                                                e.target.value
                                            )
                                    }

                                    placeholder="Opcional"
                                />

                            </CobroCampo>

                        </ModalSeccion>

                    </CobroFormulario>
                </ModalScrollContenido>


                <ModalAcciones>

                    <BotonSecundario
                        type="button"
                        onClick={handleCerrar}
                        disabled={guardando}
                    >
                        Cancelar
                    </BotonSecundario>


                    <BotonPrincipal
                        type="button"

                        onClick={
                            handleRegistrar
                        }

                        disabled={
                            guardando ||
                            cargosSeleccionados.length === 0 ||
                            !importe ||
                            Number(importe) <= 0
                        }
                    >

                        {
                            guardando

                                ? (
                                    esCorreccion
                                        ? 'Guardando corrección...'
                                        : 'Registrando...'
                                )

                                : (
                                    esCorreccion
                                        ? 'Guardar corrección'
                                        : 'Registrar cobro'
                                )
                        }

                    </BotonPrincipal>

                </ModalAcciones>

            </ModalEconomiaContenido>

        </Modal>

    );

};


export default RegistrarCobroModal;